import React, { useState } from 'react';
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Autocomplete,
  Box,
  InputAdornment,
} from '@mui/material';
import { Autorenew, QrCodeScanner } from '@mui/icons-material';
import { useGetCategoriesQuery, useGetBrandsQuery } from 'state/api';
import { BarcodeScanner } from 'components/shared';

// product.category is a fixed backend enum (not the free-form Category collection)
const CATEGORY_OPTIONS = [
  'Electronics',
  'Clothing',
  'Accessories',
  'Home & Living',
  'Books',
  'Others',
];

const UNIT_OPTIONS = ['pieces', 'kg', 'g', 'l', 'ml', 'pack'];

const GeneralSetup = ({ form, update }) => {
  const [scannerOpen, setScannerOpen] = useState(false);
  const { data: categoriesResp } = useGetCategoriesQuery({});
  const { data: brandsResp } = useGetBrandsQuery({});

  const categories = categoriesResp?.data || [];
  const brands = brandsResp?.data || [];

  // Sub-categories come from the fetched Category whose name matches the enum
  const selectedCategory = categories.find((c) => c.name === form.category);
  const subCategories = selectedCategory?.subCategories || [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        General Setup
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="category-select-label">Category*</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              label="Category*"
              value={form.category}
              onChange={(e) => {
                update('category', e.target.value);
                update('subCategory', '');
              }}
            >
              <MenuItem value="">Select category</MenuItem>
              {CATEGORY_OPTIONS.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="sub-category-select-label">Sub Category</InputLabel>
            <Select
              labelId="sub-category-select-label"
              id="sub-category-select"
              label="Sub Category"
              value={form.subCategory}
              onChange={(e) => update('subCategory', e.target.value)}
            >
              <MenuItem value="">Select Sub Category</MenuItem>
              {subCategories.map((sub) => (
                <MenuItem key={sub._id} value={sub.name}>{sub.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="brand-select-label">Brand</InputLabel>
            <Select
              labelId="brand-select-label"
              id="brand-select"
              label="Brand"
              value={form.brand}
              onChange={(e) => update('brand', e.target.value)}
            >
              <MenuItem value="">Select Brand</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand._id} value={brand.name}>{brand.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              id="product-sku"
              label="Product SKU (Code)*"
              variant="outlined"
              fullWidth
              value={form.productCode}
              onChange={(e) => update('productCode', e.target.value)}
            />
            <Tooltip title="Generate Code">
              <IconButton onClick={() => update('productCode', `PRD-${Date.now()}`)}>
                <Autorenew />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
              labelId="unit-select-label"
              id="unit-select"
              label="Unit"
              value={form.unit}
              onChange={(e) => update('unit', e.target.value)}
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            id="barcode"
            label="Barcode (8-14 digits)"
            variant="outlined"
            fullWidth
            value={form.barcode}
            onChange={(e) => update('barcode', e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Scan barcode with camera">
                    <IconButton edge="end" onClick={() => setScannerOpen(true)}>
                      <QrCodeScanner />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.tags || []}
            onChange={(e, newValue) =>
              update('tags', newValue.map((t) => t.trim()).filter(Boolean))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                id="search-tags"
                label="Search Tags"
                variant="outlined"
                fullWidth
                helperText="Type a tag and press Enter to add it"
              />
            )}
          />
        </Grid>
      </Grid>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => update('barcode', (code || '').trim())}
        title="Scan product barcode"
      />
    </Box>
  );
};

export default GeneralSetup;
